import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
	getNewContractFormSchema,
	type NewContractFormInput,
	type NewContractFormOutput,
} from "web_myplann/Goals/Forms/newContractFormSchema";
import { getNewContractMissingKey } from "web_myplann/Goals/Helpers/getNewContractMissingKey";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const DEFAULT_VALUES: NewContractFormInput = {
	type: ClientPortfolioDomainEnum.BankingProducts,
	name: "",
	value: null,
	contractNumber: "",
	producer: "",
	product: "",
};

export const useNewContractForm = (open: boolean) => {
	const { t } = useResource();

	const schema = useMemo(() => getNewContractFormSchema(t), [t]);

	const form = useForm<NewContractFormInput, unknown, NewContractFormOutput>({
		resolver: zodResolver(schema),
		defaultValues: DEFAULT_VALUES,
		mode: "onChange",
	});

	const {
		control,
		reset,
		formState: { errors },
	} = form;

	useEffect(() => {
		if (open) reset(DEFAULT_VALUES);
	}, [open, reset]);

	const type = useWatch({ control, name: "type" });
	const name = useWatch({ control, name: "name" });
	const value = useWatch({ control, name: "value" });

	const missingKey = getNewContractMissingKey(schema, name, value);
	const isTouched = name.trim().length > 0 || value !== null;
	const hasFieldError = Boolean(errors.name || errors.value);
	const missingMessage =
		missingKey && isTouched && !hasFieldError
			? t(Resources.Goals.Detail.Solution.Pin.New.Missing[missingKey])
			: null;

	const nameHint = t(Resources.Goals.Detail.Solution.Pin.New.nameHint, {
		example: t(Resources.Goals.Detail.Solution.Pin.New.NameExamples[type]),
	});

	return {
		form,
		type,
		nameHint,
		missingMessage,
		canSubmit: missingKey === null,
	};
};
